import { useEffect } from "react";
import { useOrderModifyStore } from "../stores/useOrderModifyStore";
import { toast } from "sonner";

/**
 * Hook that listens for new order modify events and shows toast notifications
 * Use this hook in your main app component or layout
 */
export const useOrderModifyToasts = () => {
  const lastEventId = useOrderModifyStore((state) => state.lastEventId);
  const events = useOrderModifyStore((state) => state.events);
  const markEventAsProcessed = useOrderModifyStore((state) => state.markEventAsProcessed);

  useEffect(() => {
    if (!lastEventId) return;

    // Find the latest unprocessed event
    const latestEvent = events.find(e => e.id === lastEventId && !e.processed);
    
    if (latestEvent) {
      // Extract meaningful info from the event data
      const eventData = latestEvent.data;
      
      // Parse the actual order modify data structure
      const getEventInfo = (data: any) => {
        try {
          const success = data?.success || false;
          const errorMessage = data?.errorMessage || null;
          
          // When success is false, order might be null
          // Try to get ticket from order if available, otherwise use a generic message
          let ticket: string | number = "Unknown";
          let symbol = "";
          
          if (data?.order && data.order !== null) {
            ticket = data.order.ticket || "Unknown";
            symbol = data.order.symbol || "";
          } else if (data?.ticket) {
            ticket = data.ticket;
            symbol = data.symbol || "";
          }
          
          const newSl = data?.newStopLoss;
          const newTp = data?.newTakeProfit;
          const previousSl = data?.previousStopLoss;
          const previousTp = data?.previousTakeProfit;
          
          return {
            success,
            errorMessage,
            ticket,
            symbol,
            newSl,
            newTp,
            previousSl,
            previousTp,
          };
        } catch (error) {
          console.error("Failed to parse order modify data:", error);
          return {
            success: false,
            errorMessage: "Failed to parse data",
            ticket: "Unknown",
            symbol: "",
            newSl: null,
            newTp: null,
            previousSl: null,
            previousTp: null,
          };
        }
      };

      const { success, errorMessage, ticket, symbol } = getEventInfo(eventData);
      
      if (success) {
        // Success toast
        let message = `✅ Trade modified successfully`;
        let description = `Order #${ticket}`;
        if (symbol) {
          description += ` (${symbol})`;
        }

        toast.success(message, {
          description: description,
          duration: 4000,
        });
      } else {
        // Error toast
        let message = `❌ Failed to modify trade`;
        let description = "";
        
        // For errors, focus on the error message rather than unknown ticket
        if (errorMessage) {
          description = errorMessage;
        } else {
          description = "Unknown error occurred";
        }
        
        // Only add ticket info if we have a valid ticket (not "Unknown")
        if (ticket && ticket !== "Unknown") {
          description = `Order #${ticket}: ${description}`;
          if (symbol) {
            description = `Order #${ticket} (${symbol}): ${errorMessage || "Unknown error occurred"}`;
          }
        }

        toast.error(message, {
          description: description,
          duration: 5000, // Longer duration for errors
        });
      }

      // Mark event as processed to avoid showing it again
      markEventAsProcessed(lastEventId);
    }
  }, [lastEventId, events, markEventAsProcessed]);

  // Return store functions for manual control if needed
  return {
    clearProcessedEvents: useOrderModifyStore((state) => state.clearProcessedEvents),
    clearAllEvents: useOrderModifyStore((state) => state.clearAllEvents),
    getUnprocessedEvents: useOrderModifyStore((state) => state.getUnprocessedEvents),
  };
};

/**
 * Alternative hook for custom toast handling
 * Returns unprocessed events for manual processing
 */
export const useOrderModifyEvents = () => {
  const unprocessedEvents = useOrderModifyStore((state) => state.getUnprocessedEvents());
  const markEventAsProcessed = useOrderModifyStore((state) => state.markEventAsProcessed);
  
  return {
    unprocessedEvents,
    markEventAsProcessed,
  };
};
